import React, { useEffect, useState } from "react"
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

import Pagination from 'pages/parts/Pagination'
import DeleteModal from 'pages/parts/DeleteModal'
import TableControl from 'pages/parts/TableControl'

import XLSX from 'xlsx-js-style'
import { styleExcelSheet } from 'helpers/excel'

import { PGET, PUT, DELETE } from 'helpers/api'
import { formatMoney, formatDate, checkRole } from 'helpers/helpers'
import MoneyInput from 'pages/parts/MoneyInput'

function Preview() {
	const { t } = useTranslation()
	const { id } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch()

	const settings = useSelector(state => state.settings)
	const reduxSettings = useSelector(state => state.settings)

	const canDelete = checkRole('POS_DOCUMENT_IN_DELETE')
	const canUpdate = checkRole('POS_NOMENKLATURA_EDIT')

	const [pageCount, setPageCount] = useState(0)
	const [data, setData] = useState({})
	const [dataDuplicate, setDataDuplicate] = useState(null)
	const [modalDelete, setModalDelete] = useState(false)
	const [deleteModalParams, setDeleteModalParams] = useState({ id: 0, index: 0 })
	const [modalTableControl, setModalTableControl] = useState(false)
	const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth <= 768)
		}
		window.addEventListener('resize', handleResize)
		return () => window.removeEventListener('resize', handleResize)
	}, [])

	async function deleteItem() {
		const response = await DELETE('/services/web/api/delete-documents-in-product/' + deleteModalParams.id, {}, { loader: true })
		if (response) {
			var dataCopy = { ...data }
			dataCopy.productList.content[deleteModalParams.index]['status'] = 2
			setData(dataCopy)
			setModalDelete(false)
		}
	}

	// Ko'rib chiqish sahifasidagi mahsulotlar ro'yxatini Excelga yuklash —
	// ro'yxat (tashqi) eksporti bilan bir xil ustun va tartibda.
	async function exportToExcel() {
		const response = await PGET(`/services/web/api/documents-in/${id}?size=10000`, {}, { loader: true })
		const doc = response?.data || {}
		const content = doc.productList?.content || []
		const excelHeaders = [[t('POS'), t('SUPPLIER'), t('CATEGORY'), t('NAME_OF_PRODUCT'), t('BARCODE'), t('UNIT'),
		t('QUANTITY'), t('PRICE'), t('WHOLESALE_PRICE'), t('SALE_PRICE'),
		t('DATE'), t('VAT'), t('IKPU'), t('TOTAL_AMOUNT')]]
		for (let i = 0; i < content.length; i++) {
			const product = content[i]
			const excelItems = []
			excelItems.push(doc.posName)
			excelItems.push(doc.organizationName)
			excelItems.push(product.categoryName || '')
			excelItems.push(product.productName)
			excelItems.push(product.barcode)
			excelItems.push(product.uomName)
			excelItems.push(product.quantity)
			excelItems.push(product.price)
			excelItems.push(product.wholesalePrice)
			excelItems.push(product.salePrice)
			excelItems.push(formatDate(product.expDate))
			excelItems.push(product.vat)
			excelItems.push(product.gtin || '')
			excelItems.push(product.totalAmount)
			excelHeaders.push(excelItems)
		}
		const ws = XLSX.utils.aoa_to_sheet(excelHeaders)
		styleExcelSheet(ws)
		const wb = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(wb, ws, "SheetJS")
		XLSX.writeFile(wb, t('GOOD_RECEPTION') + ".xlsx")
	}

	async function redirectToPriceTagsPrint() {
		var response = await PGET(`/services/web/api/documents-in/${id}?size=100000`, {}, { loader: true })
		const rate = Number(response.data.currencyRate) || 1
		const convert = (v) => (v == null ? v : Number(v) * rate)
		for (let i = 0; i < response.data.productList.content.length; i++) {
			const item = response.data.productList.content[i]
			item.salePrice = convert(item.salePrice)
			item.wholesalePrice = convert(item.wholesalePrice)
			item.bankPrice = convert(item.bankPrice)
			if (item.oldSalePrice != null) item.oldSalePrice = convert(item.oldSalePrice)
			item.salePriceOriginal = item.salePrice
			// Prices are now in the POS base currency; drop the document's per-item
			// currencyName so price-tag templates fall back to cashbox.defaultCurrency.
			delete item.currencyName
		}
		dispatch({ type: 'SET_DOCUMENT_IN_PRODUCT_LIST', payload: response.data.productList.content })
		navigate('/price-tags')
	}

	async function getData() {
		const response = await PGET("/services/web/api/documents-in/" + id, {}, { loader: true })
		if (response) {
			setPageCount(Math.ceil(response.headers['x-total-count'] / settings.size))
			setData(response.data)
		}
	}

	async function paginate(data) {
		const response = await PGET("/services/web/api/documents-in/" + id, { page: data.selected, size: settings.size })
		setData(response.data);
	}

	function updateContent(index, changes) {
		const dataCopy = { ...data }
		const content = [...dataCopy.productList.content]
		content[index] = { ...content[index], ...changes }
		dataCopy.productList = { ...dataCopy.productList, content }
		setData(dataCopy)
	}

	function startEdit(index) {
		setDataDuplicate({ ...data.productList.content[index] })
		updateContent(index, { showInput: true })
	}

	function cancelEdit(index) {
		updateContent(index, { ...dataDuplicate, showInput: false })
		setDataDuplicate(null)
	}

	function handlePriceChange(e, index) {
		updateContent(index, { [e.target.name]: e.target.value })
	}

	async function saveItem(index) {
		const item = data.productList.content[index]
		const price = Number(item.price)
		const wholesalePrice = Number(item.wholesalePrice)
		const salePrice = Number(item.salePrice)

		if (!(price > 0.0001 && wholesalePrice >= price && salePrice >= wholesalePrice)) {
			toast.dismiss()
			toast.error(t('ERROR') + ': ' + t('CHECK_FILLED_INPUTS'))
			return
		}

		const payload = {
			id: item.id,
			documentInId: item.documentInId,
			posId: item.posId,
			productId: item.productId,
			organizationId: item.organizationId,
			currencyId: item.currencyId,
			quantity: item.quantity,
			oldPrice: dataDuplicate ? Number(dataDuplicate.price) : price,
			price: price,
			wholesalePrice: wholesalePrice,
			salePrice: salePrice,
			bankPrice: item.bankPrice ? Number(item.bankPrice) : 0,
			serial: item.serial,
			expDate: item.expDate,
		}

		const response = await PUT('/services/web/api/nomenclature', payload, { loader: true })
		if (response) {
			setDataDuplicate(null)
			getData()
		}
	}

	useEffect(() => {
		getData()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			{isMobile ? (
				// MOBILE VERSION
				<div style={{ margin: '-14px', padding: '10px', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
					{/* Header - Mobile */}
					<div className="d-flex justify-content-between align-items-center mb-3">
						<h5 className="m-0 fw-bold">{t('GOOD_RECEPTION')}</h5>
						<div className="d-flex gap-2">
							<button className="btn btn-outline-primary btn-sm" onClick={() => exportToExcel()}>
								<i className="uil uil-file-download-alt me-1"></i>Excel
							</button>
							<button className="btn btn-primary btn-sm" onClick={() => redirectToPriceTagsPrint()}>
								<i className="uil uil-print me-1"></i>{t('PRINT')}
							</button>
						</div>
					</div>

					{/* Hujjat ma'lumotlari - Mobile */}
					<div className="card mb-3">
						<div className="card-body p-3">
							<h6 className="fw-bold mb-3">{t('DOCUMENT_FROM')} {formatDate(data.createdDate)}</h6>
							<div className="row g-2">
								<div className="col-6">
									<small className="text-muted">{t('POS')}</small>
									<div className="fw-500">{data.posName || '-'}</div>
								</div>
								<div className="col-6">
									<small className="text-muted">{t('SUPPLIER')}</small>
									<div className="fw-500">{data.organizationName || '-'}</div>
								</div>
								<div className="col-6">
									<small className="text-muted">{t('CURRENCY')}</small>
									<div className="fw-500">{data.currencyName || '-'}</div>
								</div>
								<div className="col-6">
									<small className="text-muted">№ {t('OVERHEAD')}</small>
									<div className="fw-500">{data.inNumber || '-'}</div>
								</div>
								{data.note &&
									<div className="col-12">
										<small className="text-muted">{t('NOTE')}</small>
										<div className="fw-500">{data.note}</div>
									</div>
								}
							</div>
						</div>
					</div>

					{/* Mahsulotlar ro'yxati - Mobile */}
					<div className="card mb-3">
						<div className="card-body p-2">
							<div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
								<strong>{t('PRODUCTS')}</strong>
								<i className="uil-setting fz-20 cursor text-primary" onClick={() => setModalTableControl(true)}></i>
							</div>

							{data.productList?.content.map((item, index) => (
								<div key={index} className="border-bottom p-2 mb-2">
									{/* Mahsulot nomi va status */}
									<div className="d-flex justify-content-between align-items-start mb-2">
										<div className="fw-bold">{item.rowNum}. {item.productName}</div>
										<div className="d-flex gap-2">
											{item.showInput ?
												<>
													<div className="table-action-button table-action-primary-button" onClick={() => saveItem(index)}>
														<i className="uil-check"></i>
													</div>
													<div className="table-action-button table-action-danger-button" onClick={() => cancelEdit(index)}>
														<i className="uil-times"></i>
													</div>
												</>
												:
												<>
													{(canUpdate && item.status !== 2 && dataDuplicate === null) &&
														<div className="table-action-button table-action-primary-button" onClick={() => startEdit(index)}>
															<i className="uil-edit-alt"></i>
														</div>
													}
													{item.status === 1 &&
														<span className="badge bg-danger">{t('USED')}</span>
													}
													{item.status === 2 &&
														<span className="badge bg-danger">{t('DELETED')}</span>
													}
													{(item.status === 0 && canDelete) &&
														<div className="table-action-button table-action-danger-button"
															onClick={() => { setDeleteModalParams({ id: item.id, index: index }); setModalDelete(true) }}>
															<i className="uil uil-times"></i>
														</div>
													}
												</>
											}
										</div>
									</div>

									{/* IKPU va Shtrix-kod */}
									<div className="d-flex justify-content-between small text-muted mb-2">
										<span><strong>{t('IKPU')}:</strong> {item.gtin || '-'}</span>
										<span><strong>{t('BARCODE')}:</strong> {item.barcode || '-'}</span>
									</div>

									{/* Artikul */}
									{(!reduxSettings.tableDocumentsInPreview || reduxSettings.tableDocumentsInPreview?.artikul) &&
										<div className="small text-muted mb-2">
											<strong>{t('ARTIKUL')}:</strong> {item.artikul || '-'}
										</div>
									}

									{/* Asosiy ma'lumotlar */}
									<div className="row g-2 mb-2">
										<div className="col-4">
											<small className="text-muted">{t('QUANTITY')}</small>
											<div className="fw-500">{formatMoney(item.quantity)} {item.uomName}</div>
										</div>
										<div className="col-4">
											<small className="text-muted">{t('ADMISSION_PRICE')}</small>
											{item.showInput ?
												<MoneyInput name="price" className="form-control"
													value={item.price} onChange={(e) => handlePriceChange(e, index)} />
												: <div className="fw-500">{formatMoney(item.price)}</div>}
										</div>
										<div className="col-4">
											<small className="text-muted">{t('UNIT_PRICE')}</small>
											{item.showInput ?
												<MoneyInput name="salePrice" className="form-control"
													value={item.salePrice} onChange={(e) => handlePriceChange(e, index)} />
												: <div className="fw-500">{formatMoney(item.salePrice)}</div>}
										</div>
									</div>

									{/* Qo'shimcha ma'lumotlar - nastroykaga qarab */}
									<div className="row g-2 mb-2">
										{/* Ulgurji narx */}
										{(!reduxSettings.tableDocumentsInPreview || reduxSettings.tableDocumentsInPreview?.wholesalePrice) &&
											<div className="col-4">
												<small className="text-muted">{t('WHOLESALE_PRICE')}</small>
												{item.showInput ?
													<MoneyInput name="wholesalePrice" className="form-control"
														value={item.wholesalePrice} onChange={(e) => handlePriceChange(e, index)} />
													: <div className="fw-500">{formatMoney(item.wholesalePrice)}</div>}
											</div>
										}
										{/* Bank narxi */}
										{(!reduxSettings.tableDocumentsInPreview || reduxSettings.tableDocumentsInPreview?.bankPrice) &&
											<div className="col-4">
												<small className="text-muted">{t('BANK_PRICE')}</small>
												{item.showInput ?
													<MoneyInput name="bankPrice" className="form-control"
														value={item.bankPrice} onChange={(e) => handlePriceChange(e, index)} />
													: <div className="fw-500">{formatMoney(item.bankPrice)}</div>}
											</div>
										}
										{/* QQS */}
										{(!reduxSettings.tableDocumentsInPreview || reduxSettings.tableDocumentsInPreview?.vat) &&
											<div className="col-4">
												<small className="text-muted">{t('VAT')}</small>
												<div className="fw-500">{item.vat || '-'}</div>
											</div>
										}
									</div>

									{/* Seriya */}
									{(!reduxSettings.tableDocumentsInPreview || reduxSettings.tableDocumentsInPreview?.serial) &&
										<div className="small mb-2">
											<span className="text-muted">{t('SERIAL')}:</span> {item.serial || '-'}
										</div>
									}

									{/* Muddati */}
									{(!reduxSettings.tableDocumentsInPreview || reduxSettings.tableDocumentsInPreview?.expDate) &&
										<div className="small mb-2">
											<span className="text-muted">{t('EXPIRATION_DATE')}:</span> {formatDate(item.expDate) || '-'}
										</div>
									}

									{/* Markirovka */}
									{(!reduxSettings.tableDocumentsInPreview || reduxSettings.tableDocumentsInPreview?.marking) &&
										<div className="d-flex justify-content-between small mb-2">
											<span className="text-muted">{t('MARKING')}:</span>
											{item.marking ?
												<span className="badge bg-primary">{t('YES')}</span>
												:
												<span className="badge bg-secondary">{t('NO')}</span>
											}
										</div>
									}

									{/* Summa */}
									<div className="d-flex justify-content-end mt-2">
										<span className="fw-bold text-primary">
											{t('AMOUNT')}: {formatMoney(item.totalAmount)}
										</span>
									</div>
								</div>
							))}
						</div>
					</div>

					{/* Jami summa - Mobile */}
					<div className="card mb-3">
						<div className="card-body p-3">
							<div className="d-flex justify-content-between mb-2">
								<span className="text-muted">{t('RECEIPT_AMOUNT')}:</span>
								<span className="fw-bold">{formatMoney(data.sumPrice)} {data.currencyName}</span>
							</div>
							<div className="d-flex justify-content-between mb-2">
								<span className="text-muted">{t('SALE_AMOUNT')}:</span>
								<span className="fw-bold">{formatMoney(data.sumSalePrice)} {data.currencyName}</span>
							</div>
							<div className="d-flex justify-content-between">
								<span className="text-muted">{t('INCOME')}:</span>
								<span className="fw-bold text-success">{formatMoney(data.sumDifference)} {data.currencyName}</span>
							</div>
						</div>
					</div>

					{/* Pagination va Orqaga tugma - Mobile */}
					<div className="d-flex justify-content-between align-items-center">
						<Pagination pageCount={pageCount} paginate={paginate}></Pagination>
						<button type="button" className="btn btn-warning btn-sm" onClick={() => navigate(-1)}>
							{t('CANCEL')}
						</button>
					</div>
				</div>
			) : (
				// DESKTOP VERSION
				<>
					<div className="page-header d-flex justify-content-between mb-2">
						<div className="d-flex text-truncate">
							<h4 className="vertical-center">
								{t('GOOD_RECEPTION')}
							</h4>
							<div className="vertical-center mt-1">
								<i className="uil uil-angle-double-right fz-20"></i>
							</div>
							<div className="vertical-center mt-1">
								<h6>{t('PREVIEW')}</h6>
							</div>
						</div>
						<div className="d-flex">
							<span className="btn-rounded btn btn-outline-primary me-2" onClick={() => exportToExcel()}>
								<i className="uil uil-file-download-alt me-2"></i>Excel
							</span>
							<span className="btn-rounded btn btn-primary" onClick={() => redirectToPriceTagsPrint()}>
								{t('PRINT')}
							</span>
						</div>
					</div>

					<div className="card mb-3">
						<div className="card-body">
							<h4 className="fw-600 mb-3">{t('DOCUMENT_FROM')} {formatDate(data.createdDate)}</h4>
							<div className="d-flex flex-wrap">
								<div className="me-3">
									<div className="d-flex">
										<div className="fw-600 text-nowrap me-3">{t('POS')}:</div>
										<div className="w-100 text-end text-nowrap">{data.posName}</div>
									</div>
									<div className="d-flex">
										<div className="fw-600 text-nowrap me-3">{t('SUPPLIER')}:</div>
										<div className="w-100 text-end text-nowrap">{data.organizationName}</div>
									</div>
									<div className="d-flex">
										<div className="fw-600 text-nowrap me-3">{t('CURRENCY')}:</div>
										<div className="w-100 text-end text-nowrap">{data.currencyName}</div>
									</div>
								</div>
								<div className="me-3">
									<div className="d-flex">
										<div className="fw-600 text-nowrap me-3">№ {t('OVERHEAD')}:</div>
										<div className="w-100 text-end text-nowrap">{data.inNumber}</div>
									</div>
									<div className="d-flex">
										<div className="fw-600 text-nowrap me-3">{t('NOTE')}:</div>
										<div className="w-100 text-end text-nowrap">{data.note}</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="card">
						<div className="card-body">
							<div className="table-responsive">
								<table className="table table-striped">
									<thead>
										<tr>
											<th>{t('NAME_OF_PRODUCT')}</th>
											<th className="text-center">{t('IKPU')}</th>
											<th className="text-center">{t('BARCODE')}</th>
											{(!reduxSettings.tableDocumentsInPreview || reduxSettings.tableDocumentsInPreview?.artikul) &&
												<th className="text-center">{t('ARTIKUL')}</th>
											}
											{(!reduxSettings.tableDocumentsInPreview || reduxSettings.tableDocumentsInPreview?.serial) &&
												<th className="text-center">{t('SERIAL')}</th>
											}
											{(!reduxSettings.tableDocumentsInPreview || reduxSettings.tableDocumentsInPreview?.expDate) &&
												<th className="text-center">{t('EXPIRATION_DATE')}</th>
											}
											<th className="text-center">{t('QUANTITY')}</th>
											<th className="text-center">{t('ADMISSION_PRICE')}</th>

											{(!reduxSettings.tableDocumentsInPreview || reduxSettings.tableDocumentsInPreview?.wholesalePrice) &&
												<th className="text-center">{t('WHOLESALE_PRICE')}</th>
											}
											{(!reduxSettings.tableDocumentsInPreview || reduxSettings.tableDocumentsInPreview?.bankPrice) &&
												<th className="text-center">{t('BANK_PRICE')}</th>
											}
											<th className="text-center">{t('UNIT_PRICE')}</th>
											{(!reduxSettings.tableDocumentsInPreview || reduxSettings.tableDocumentsInPreview?.vat) &&
												<th className="text-center">{t('VAT')}</th>
											}
											<th className="text-center">{t('AMOUNT')}</th>
											{(!reduxSettings.tableDocumentsInPreview || reduxSettings.tableDocumentsInPreview?.marking) &&
												<th className="text-center">{t('MARKING')}</th>
											}
											<th className="text-center table-control">
												<i className="uil-setting table-control-icon" onClick={() => setModalTableControl(true)}></i>
												{t('ACTION')}
											</th>
										</tr>
									</thead>
									<tbody>
										{data.productList?.content.map((item, index) => (
											<tr key={index}>
												<td>{item.rowNum}. {item.productName}</td>
												<td className="text-center">{item.gtin}</td>
												<td className="text-center">{item.barcode}</td>
												{(!reduxSettings.tableDocumentsInPreview || reduxSettings.tableDocumentsInPreview?.artikul) &&
													<td className="text-center">{item.artikul}</td>
												}
												{(!reduxSettings.tableDocumentsInPreview || reduxSettings.tableDocumentsInPreview?.serial) &&
													<td className="text-center">{item.serial}</td>
												}
												{(!reduxSettings.tableDocumentsInPreview || reduxSettings.tableDocumentsInPreview?.expDate) &&
													<td className="text-center">{formatDate(item.expDate)}</td>
												}
												<td className="text-center">{formatMoney(item.quantity)} {item.uomName}</td>
												<td className="text-center">
													{item.showInput ?
														<MoneyInput name="price" className="form-control" style={{ minWidth: '90px' }}
															value={item.price} onChange={(e) => handlePriceChange(e, index)} />
														: formatMoney(item.price)}
												</td>

												{(!reduxSettings.tableDocumentsInPreview || reduxSettings.tableDocumentsInPreview?.wholesalePrice) &&
													<td className="text-center">
														{item.showInput ?
															<MoneyInput name="wholesalePrice" className="form-control" style={{ minWidth: '90px' }}
																value={item.wholesalePrice} onChange={(e) => handlePriceChange(e, index)} />
															: formatMoney(item.wholesalePrice)}
													</td>
												}
												{(!reduxSettings.tableDocumentsInPreview || reduxSettings.tableDocumentsInPreview?.bankPrice) &&
													<td className="text-center">
														{item.showInput ?
															<MoneyInput name="bankPrice" className="form-control" style={{ minWidth: '90px' }}
																value={item.bankPrice} onChange={(e) => handlePriceChange(e, index)} />
															: formatMoney(item.bankPrice)}
													</td>
												}
												<td className="text-center">
													{item.showInput ?
														<MoneyInput name="salePrice" className="form-control" style={{ minWidth: '90px' }}
															value={item.salePrice} onChange={(e) => handlePriceChange(e, index)} />
														: formatMoney(item.salePrice)}
												</td>
												{(!reduxSettings.tableDocumentsInPreview || reduxSettings.tableDocumentsInPreview?.vat) &&
													<td className="text-center">{item.vat}</td>
												}
												<td className="text-center">{formatMoney(item.totalAmount)}</td>
												{(!reduxSettings.tableDocumentsInPreview || reduxSettings.tableDocumentsInPreview?.marking) &&
													<td className="text-center">{item.marking ? t('YES') : t('NO')}</td>
												}
												<td className="d-flex justify-content-center">
													{item.showInput ?
														<>
															<div className="table-action-button table-action-primary-button me-2" onClick={() => saveItem(index)}>
																<i className="uil-check"></i>
															</div>
															<div className="table-action-button table-action-danger-button" onClick={() => cancelEdit(index)}>
																<i className="uil-times"></i>
															</div>
														</>
														:
														<>
															{item.status === 2 ?
																<span className="text-danger">{t('DELETED')}</span>
																:
																<>
																	{(canUpdate && dataDuplicate === null) &&
																		<div className="table-action-button table-action-primary-button me-2" onClick={() => startEdit(index)}>
																			<i className="uil-edit-alt"></i>
																		</div>
																	}
																	{item.status === 1 &&
																		<span className="text-danger vertical-center">{t('USED')}</span>
																	}
																	{(item.status === 0 && canDelete) &&
																		<div className="table-action-button table-action-danger-button"
																			onClick={() => { setDeleteModalParams({ id: item.id, index: index }); setModalDelete(true) }}>
																			<i className="uil uil-times"></i>
																		</div>
																	}
																</>
															}
														</>
													}
												</td>
											</tr>
										))
										}
									</tbody>
								</table>
							</div>

							<div className="w-100 d-flex justify-content-end">
								<div className="flex-column w-25">
									<h5 className="d-flex justify-content-between">
										<strong className="me-2">{t('RECEIPT_AMOUNT')}:</strong>{formatMoney(data.sumPrice)} {data.currencyName}
									</h5>
									<h5 className="d-flex justify-content-between">
										<strong className="me-2">{t('SALE_AMOUNT')}:</strong>{formatMoney(data.sumSalePrice)} {data.currencyName}
									</h5>
									<h5 className="d-flex justify-content-between">
										<strong className="me-2">{t('INCOME')}:</strong>{formatMoney(data.sumDifference)} {data.currencyName}
									</h5>
								</div>
							</div>

							<div className="d-flex justify-content-between mt-5">
								<div className="vertical-end">
									<Pagination pageCount={pageCount} paginate={paginate}></Pagination>
								</div>
								<div>
									<button type="button" className="btn btn-warning btn-rounded btn-wide" onClick={() => navigate(-1)}>
										{t('CANCEL')}
									</button>
								</div>
							</div>
						</div>
					</div>
				</>
			)}

			<DeleteModal
				modalDelete={modalDelete}
				setModalDelete={setModalDelete}
				deleteItem={deleteItem}>
			</DeleteModal>

			{/* MODAL TABLE CONTROL */}
			<TableControl
				modalTableControl={modalTableControl}
				setModalTableControl={setModalTableControl}
				keyName={'tableDocumentsInPreview'}>
			</TableControl>
			{/* MODAL TABLE CONTROL */}
		</>
	)
}

export default Preview
